import { Component, ChangeDetectionStrategy, inject, signal, OnInit, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { InvoiceService, type PublicInvoiceDetailDTO, type ChargeDTO } from '../../../services/invoice.service';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { PaymentLinkStatusEnum } from '@/src/core/models';

@Component({
  selector: 'app-public-invoice-detail',
  templateUrl: './public-invoice-detail.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, TagModule, ButtonModule]
})
export class PublicInvoiceDetailComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private invoiceService = inject(InvoiceService);

  detail = signal<PublicInvoiceDetailDTO | null>(null);
  loading = signal(true);
  error = signal(false);
  showScheduledPayments = signal(false);

  // Cargos que no son programados (para la lista principal)
  currentCharges = computed(() => {
    const d = this.detail();
    if (!d?.charges) return [];
    return d.charges.filter(c => c.status !== 'SCHEDULED');
  });

  // Cargos programados (para la sección colapsable)
  scheduledCharges = computed(() => {
    const d = this.detail();
    if (!d?.charges) return [];
    return d.charges.filter(c => c.status === 'SCHEDULED');
  });

  ngOnInit(): void {
    const invoiceId = this.route.snapshot.paramMap.get('invoiceId');
    const hash = this.route.snapshot.queryParamMap.get('hash');

    if (!invoiceId || !hash) {
      this.loading.set(false);
      this.error.set(true);
      return;
    }

    const id = Number(invoiceId);
    if (Number.isNaN(id)) {
      this.loading.set(false);
      this.error.set(true);
      return;
    }

    this.invoiceService.getPublicInvoiceDetail(id, hash).subscribe({
      next: (res) => {
        this.loading.set(false);
        if (res?.body) {
          this.detail.set(res.body);
        } else {
          this.error.set(true);
        }
      },
      error: () => {
        this.loading.set(false);
        this.error.set(true);
      }
    });
  }

  isChargePending(charge: ChargeDTO): boolean {
    return charge?.status === PaymentLinkStatusEnum.PENDING || charge?.status === PaymentLinkStatusEnum.DUE;
  }

  getPaymentLinkUrl(charge: ChargeDTO): string {
    return charge?.paymentLink?.link ?? '#';
  }

  hasPendingChargesWithLink(): boolean {
    const d = this.detail();
    if (!d?.charges?.length) return false;
    return d.charges.some(
      (c) => this.isChargePending(c) && c.paymentLink?.link
    );
  }

  formatAmount(amount: number | undefined, currency: string): string {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: currency || 'MXN'
    }).format(amount ?? 0);
  }

  formatDate(isoDate: string | undefined): string {
    if (!isoDate) return '—';
    return new Intl.DateTimeFormat('es-MX', {
      dateStyle: 'medium'
    }).format(new Date(isoDate));
  }

  getChargeStatusLabel(status: string | undefined): string {
    const labels: Record<string, string> = {
      PENDING: 'Pendiente',
      PAID: 'Pagado',
      SCHEDULED: 'Programado',
      DUE: 'Vencido',
      FAILED: 'Fallido',
      EXPIRED: 'Expirado',
      CANCELLED: 'Cancelado'
    };
    return status ? labels[status] ?? status : '—';
  }

  getChargeStatusSeverity(status: string | undefined): 'success' | 'danger' | 'warn' | 'info' | 'secondary' | null {
    const map: Partial<Record<string, 'success' | 'danger' | 'warn' | 'info' | 'secondary'>> = {
      PENDING: 'warn',
      PAID: 'success',
      SCHEDULED: 'info',
      DUE: 'danger',
      FAILED: 'danger',
      EXPIRED: 'secondary',
      CANCELLED: 'secondary'
    };
    const severity = status ? map[status] : undefined;
    return severity ?? null;
  }

  getCustomerName(sale: PublicInvoiceDetailDTO['sale']): string {
    const c = sale?.customer;
    if (!c) return '—';
    return c.comercialName || c.legalName || [c.firstName, c.lastName].filter(Boolean).join(' ') || '—';
  }
}
